import { Component } from '@theme/component';

/**
 * @typedef {Object} AxSlidersProductsRefs
 * @property {HTMLElement} [content]
 * @property {HTMLElement} [carousel]
 */

/**
 * @extends {Component<AxSlidersProductsRefs>}
 */
class AxSlidersProducts extends Component {
  /** @type {any} */
  #swiper = null;

  /** @type {number} Nombre de colonnes réglé dans la section, avant tout ajustement. */
  #columns = 0;

  /** @type {string} Valeur inline d'origine de `--column-count` ('' si héritée). */
  #columnsInline = '';

  /** @type {string} Valeur inline d'origine de `--peek-next-slide-size` ('' si héritée). */
  #peekInline = '';

  connectedCallback() {
    super.connectedCallback();
    this.#initSwiperIfPresent();
    // When Nosto sources the slides, the embedded section script fires
    // this event once the placement has been fetched and slot handles
    // assigned (or excess <swiper-slide> wrappers removed). Re-measure
    // so widths and peek are correct for the actual slide count.
    this.addEventListener('ax-sliders-products:slides-updated', this.#onSlidesUpdated);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('ax-sliders-products:slides-updated', this.#onSlidesUpdated);
    this.#destroySwiper();
  }

  #onSlidesUpdated = () => {
    this.#fitSlidesToWidth();
    this.#swiper?.update();
  };

  /**
   * Le réglage `columns` dimensionne les slides pour N tuiles, mais Nosto peut en
   * servir moins (les emplacements en trop sont retirés) : les slides restantes
   * garderaient alors la largeur d'une grille de N et laisseraient un vide à droite.
   * On recale la largeur sur le nombre de slides réellement présentes, et on annule
   * la réserve d'aperçu quand il n'y a plus rien à faire dépasser.
   */
  #fitSlidesToWidth() {
    const carousel = this.refs.carousel;
    if (!carousel || !this.#columns) return;

    const count = carousel.querySelectorAll('swiper-slide').length;
    if (!count) return;

    if (count < this.#columns) {
      carousel.style.setProperty('--column-count', String(count));
    } else if (this.#columnsInline) {
      carousel.style.setProperty('--column-count', this.#columnsInline);
    } else {
      carousel.style.removeProperty('--column-count');
    }

    if (count <= this.#columns) {
      carousel.style.setProperty('--peek-next-slide-size', '0px');
    } else if (this.#peekInline) {
      carousel.style.setProperty('--peek-next-slide-size', this.#peekInline);
    } else {
      carousel.style.removeProperty('--peek-next-slide-size');
    }
  }

  async #initSwiperIfPresent() {
    await customElements.whenDefined('swiper-container');

    const carousel = this.refs.carousel;
    if (!carousel || this.#swiper) return;

    const swiperEl = /** @type {HTMLElement & { swiper: any; initialize: () => void }} */ (
      carousel.querySelector('swiper-container.ax-product-recommendations__swiper')
    );
    const fallback = carousel.querySelector('[data-ax-fallback]');
    if (!swiperEl || !fallback) return;

    const slideElements = [...fallback.children];
    if (!slideElements.length) return;

    this.#columnsInline = carousel.style.getPropertyValue('--column-count');
    this.#peekInline = carousel.style.getPropertyValue('--peek-next-slide-size');
    this.#columns = parseInt(getComputedStyle(carousel).getPropertyValue('--column-count'), 10) || 0;

    const reservedHeight = carousel.getBoundingClientRect().height;
    if (reservedHeight > 0) {
      carousel.style.minHeight = `${reservedHeight}px`;
    }

    for (const slideEl of slideElements) {
      const swiperSlide = document.createElement('swiper-slide');
      swiperSlide.appendChild(slideEl);
      swiperEl.appendChild(swiperSlide);
    }

    fallback.remove();

    const gapDesktop = parseInt(swiperEl.dataset.gapDesktop ?? '0', 10) || 0;
    const gapMobile = parseInt(swiperEl.dataset.gapMobile ?? '0', 10) || 0;

    Object.assign(swiperEl, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      speed: 400,
      grabCursor: true,
      watchOverflow: true,
      a11y: true,
      keyboard: { enabled: true },
      spaceBetween: gapMobile,
      breakpoints: {
        750: { spaceBetween: gapDesktop },
      },
    });

    swiperEl.initialize();
    swiperEl.removeAttribute('init');

    requestAnimationFrame(() => {
      carousel.style.minHeight = '';
    });

    this.#swiper = swiperEl.swiper;
    this.#fitSlidesToWidth();
    this.#swiper?.update();
  }

  #destroySwiper() {
    if (!this.#swiper) return;
    try {
      this.#swiper.destroy(true, true);
    } catch (_) {
      // Swiper can throw during teardown if already destroyed.
    }
    this.#swiper = null;
  }
}

if (!customElements.get('ax-sliders-products')) {
  customElements.define('ax-sliders-products', AxSlidersProducts);
}
